import { Chip } from '@hologram-dimension/chip';
import Table from 'components/Table/Table';
import { ReactNode, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Highlight, useSortBy, useInfiniteHits, useInstantSearch } from 'react-instantsearch';
import { Popover } from '@hologram-dimension/popover';
import { Icon } from '@hologram-dimension/icon';
import clsx from 'clsx/lite';
import { IconNames } from '@hologram-dimension/icons';
import type { ProfilesDaoSimProfileDetails } from 'types/__generated__/SimInventoryApi';
import { TableRowItem } from 'components/Table/types';
import formatMoney from 'utils/numbers/formatMoney';
import useBillingRates from 'api/billing/useBillingRates';
import LoaderCell from 'components/SimInventoryTable/ColumnDefinition/LoaderCell';
import useUserInfo from 'hooks/User/useUserInfo';
import { LegacyPlan, SingleLegacyPlan } from 'types/legacyDashboard';
import useSimBilling from 'api/sims/useSimBilling';
import type { CoverageSearchVariant, AlgoliaNetwork } from './searchTypes';
import { ALGOLIA_INDEX, reorderRATs, transformRAT } from './searchUtils';
import styles from './Hits.module.css';
import useCoverageProfiles from './useCoverageProfiles';

type Sort = {
  attribute: 'network' | 'country' | 'relevant';
  direction?: 'asc' | 'desc';
};
export interface HitsProps {
  lastKnownNetwork?: string;
  excludeColumns?: string[];
  columns: {
    key: string;
    label: string;
    width?: number;
    sortableAttribute?: Sort['attribute'];
  }[];
  simProfiles?: ProfilesDaoSimProfileDetails[];
  planProfiles?: SingleLegacyPlan['profiles'];
  orgId?: OrgId;
  variant?: CoverageSearchVariant;
  hologramSimId?: HologramSimId;
  legacyPlan?: LegacyPlan;
}
const Hits = (props: HitsProps) => {
  const {
    lastKnownNetwork,
    excludeColumns,
    columns,
    simProfiles,
    planProfiles,
    orgId,
    variant,
    hologramSimId,
    legacyPlan,
  } = props;
  const { items, results, isLastPage, showMore } = useInfiniteHits<AlgoliaNetwork>();
  const { status } = useInstantSearch();
  const sentinelRef = useRef<HTMLTableCellElement>(null);
  const {
    billingRates,
    error: billingRatesError,
    isLoading: isBillingRatesLoading,
  } = useBillingRates(orgId);
  const {
    simBilling,
    error: simBillingError,
    isLoading: isSimBillingLoading,
  } = useSimBilling(hologramSimId);
  const { isSuperAdmin } = useUserInfo();

  const getDataRate = useCallback(
    (code: AlgoliaNetwork['code'], rackRate: AlgoliaNetwork['data_rate']) => {
      if (isBillingRatesLoading || isSimBillingLoading) {
        return <LoaderCell noTranslate />;
      }
      if (billingRatesError || simBillingError) {
        return <span className={styles.diminished}>—</span>;
      }
      if (billingRates) {
        if (billingRates.rateType === 'flat_rate') {
          if (legacyPlan) {
            return formatMoney(legacyPlan.overageCharge);
          }
          return formatMoney(simBilling.dataRate ?? simBilling.overageCharge);
        }
        const matchingRate = billingRates.data?.find((rate) => rate.unc === code);
        if (matchingRate)
          return (
            <>
              {isSuperAdmin && <span className={styles.superAdminRate}>&bull;&nbsp;</span>}
              {formatMoney(matchingRate.costPerMb)}
            </>
          );
      }
      return formatMoney(rackRate);
    },
    [
      billingRates,
      billingRatesError,
      isBillingRatesLoading,
      isSimBillingLoading,
      isSuperAdmin,
      simBilling.dataRate,
      simBilling.overageCharge,
      simBillingError,
      legacyPlan,
    ]
  );

  useEffect(() => {
    if (sentinelRef.current !== null) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !isLastPage && status === 'idle') {
              showMore();
            }
          });
        },
        {
          rootMargin: '200px 0px',
        }
      );

      observer.observe(sentinelRef.current);

      return () => observer.disconnect();
    }
    return undefined;
  }, [showMore, isLastPage, status]);

  const sortOptions = [
    // we don't actually use the labels, but the types require it
    { label: 'Most relevant', value: ALGOLIA_INDEX },
    { label: 'Country (asc)', value: 'networks-v2_country_asc' },
    { label: 'Country (desc)', value: 'networks-v2_country_desc' },
    { label: 'Network (asc)', value: 'networks-v2_name_asc' },
    { label: 'Network (desc)', value: 'networks-v2_name_desc' },
  ];
  const { refine } = useSortBy({ items: sortOptions });

  const [currentSort, setCurrentSort] = useState<Sort>({ attribute: 'network', direction: 'asc' });

  const handleSort = (attribute: Sort['attribute']) => {
    if (!currentSort.direction || attribute !== currentSort.attribute) {
      return setCurrentSort({ attribute, direction: 'asc' });
    }
    if (currentSort.direction === 'asc') {
      return setCurrentSort({ attribute, direction: 'desc' });
    }
    return setCurrentSort({ attribute: 'network', direction: 'asc' });
  };

  useEffect(() => {
    if (currentSort.attribute === 'relevant') return refine(ALGOLIA_INDEX);
    switch (currentSort.attribute) {
      case 'network':
        return refine(`networks-v2_name_${currentSort.direction}`);
      case 'country':
      default:
        return refine(`networks-v2_country_${currentSort.direction}`);
    }
  }, [currentSort, refine]);

  const getSortIcon: (attribute: Sort['attribute']) => IconNames = (attribute) => {
    if (currentSort.attribute === attribute) {
      if (currentSort.direction === 'asc') {
        return 'ArrowTriangleNorth';
      }
      return 'ArrowTriangleSouth';
    }
    return 'ArrowTriangleNorthSouth';
  };
  const profiles = useCoverageProfiles({ simProfiles, planProfiles });
  const columnsToShow = columns.filter((column) => excludeColumns?.indexOf(column.key) ?? -1 < 0);

  const rows = useMemo(() => {
    const tableRows: TableRowItem[] = [];

    items.forEach((network) => {
      const {
        objectID,
        data_rate: dataRate,
        code,
        offer_id: offerId,
        country,
        supported_rat: supportedRats,
      } = network;

      tableRows.push({
        key: objectID,
        className: styles.row,
        cells: columnsToShow.map((column) => {
          let children: ReactNode;
          let className: string | null = null;
          switch (column.key) {
            case 'name':
              children = (
                <span className={styles.networkName}>
                  <Highlight
                    attribute="name"
                    hit={network}
                    classNames={{ highlighted: styles.highlight }}
                  />
                  {objectID === lastKnownNetwork && (
                    <>
                      {' '}
                      <Popover
                        id="last-seen-popover"
                        content="This is the network this SIM last connected to."
                      >
                        <Chip iconStart="CellTower" title="Last seen" colorVariant="teal">
                          Last seen
                        </Chip>
                      </Popover>
                    </>
                  )}
                </span>
              );
              break;
            case 'profile':
              children = profiles
                ? profiles?.find((profile) => offerId.includes(profile.offerId))?.profileName
                : '—'; // TODO
              break;
            case 'rate':
              children = getDataRate(code, dataRate);
              className = styles.numericColumn;
              break;
            case 'country':
              children = (
                <Highlight
                  attribute="country"
                  hit={network}
                  classNames={{ highlighted: styles.highlight }}
                  title={country}
                />
              );
              className = clsx(
                styles.countryColumn,
                variant === 'modal' && styles.modalCountryColumn
              );
              break;
            case 'rats':
              children = (
                <div className={styles.ratList}>
                  {reorderRATs(supportedRats).map((rat) => (
                    <Chip size="small" key={rat}>
                      {transformRAT(rat)}
                    </Chip>
                  ))}
                </div>
              );
              break;
            default:
          }

          return {
            key: column.key,
            children,
            className: clsx(className, styles.bodyCell),
          };
        }),
      });
    });

    return tableRows;
  }, [items, columnsToShow, lastKnownNetwork, getDataRate, profiles, variant]);

  return (
    <Table
      wrapperClassName={styles.tableWrapper}
      theadClassName={styles.header}
      headers={columnsToShow.map((item) => {
        let children: ReactNode = item.label;
        if (item.sortableAttribute) {
          children = (
            <button
              type="button"
              onClick={() => handleSort(item.sortableAttribute!)}
              className={clsx(currentSort.attribute === item.sortableAttribute && styles.sorted)}
            >
              {item.label}{' '}
              <Icon
                size="small"
                name={getSortIcon(item.sortableAttribute)}
                className={styles.sortIcon}
                color="currentColor"
              />
            </button>
          );
        }
        return {
          key: item.key,
          children,
          className: clsx(styles.headerCell, item.key === 'rate' && styles.numericColumn),
          width: item.width,
        };
      })}
      rows={rows}
      footer={
        <tr>
          <td colSpan={columns.length} ref={sentinelRef} className={styles.tableFooter}>
            <span className={styles.footerCount}>
              {results?.nbHits} total {results?.nbHits === 1 ? 'network' : 'networks'}
              {(status === 'loading' || status === 'stalled') && <> Loading&hellip;</>}
            </span>
          </td>
        </tr>
      }
    />
  );
};

export default Hits;
